import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Grid, Stack, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { useFields } from '@pbt/pbt-ui-components'

import { ContextualBackButton } from '~/components/common/buttons/ContextualBackButton'
import { ChatContainerProvider } from '~/components/dashboard/communications/conversations-chat/context/ChatContainerContext'
import { RightRailOverlayProvider } from '~/components/dashboard/soapV2/context/RightRailOverlayContext'
import FeatureToggle from '~/constants/featureToggle'
import { fetchClient } from '~/store/actions/clients'
import { fetchMember } from '~/store/actions/members'
import { getClientIsLoading } from '~/store/reducers/clients'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getConversationById,
  getConversationsIsNotAccessibleFromBusinessErrorType,
} from '~/store/reducers/conversations'
import { getUser } from '~/store/reducers/users'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import Chat from './Chat'
import ChatHeader from './header/ChatHeader'
import KibChatHeader from './header/KibChatHeader'
import KibMessageFormattingArea from './KibMessageFormattingArea'
import MessageFormattingArea from './MessageFormattingArea'
import ChatRightRail from './right-rail/ChatRightRail'
import CommunicationsPatientMedicalRecordRightRail from './right-rail/CommunicationsPatientMedicalRecordRightRail'

const useStyles = makeStyles(
  (theme) => ({
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.constants.progressBarHeight}px)`,
      width: '100%',
      overflow: 'hidden',
      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: '7fr 5fr',
      },
    },
    mainContentGrid: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.brandPageBg,
      overflow: 'hidden',
      minWidth: 0,
    },
    legacyContainer: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.brandPageBg,
      height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.constants.progressBarHeight}px)`,
      width: '100%',
      overflow: 'hidden',
    },
    legacyMainContent: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.brandPageBg,
      height: '100%',
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: `calc(100% - ${theme.constants.rightRailCollapsedWidth}px)`,
      },
    },
    rightRailGrid: {
      display: 'none',
      overflow: 'hidden',
      [theme.breakpoints.up('lg')]: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    root: {
      backgroundColor: theme.colors.brandPageBg,
      height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.constants.progressBarHeight}px)`,
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: `calc(100% - ${theme.constants.rightRailCollapsedWidth}px)`,
      },
    },
    rootWide: {
      width: '100%',
    },
    chat: {
      flex: 1,
      overflow: 'auto',
    },
    backPanel: {
      backgroundColor: theme.colors.grayGray4,
      paddingLeft: theme.spacing(3),
    },
    chatHeader: {
      height: 73,
      padding: theme.spacing(1, 3),
      borderRadius: 2,
      borderBottom: theme.constants.tableBorder,
      backgroundColor: theme.colors.grayWhite,
      boxShadow: theme.constants.listItemShadow,
    },
    transportIcon: {
      color: theme.colors.grayGray3,
      fontSize: '3.6rem',
    },
  }),
  { name: 'ChatContainer' },
)

const ChatContainer = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { conversationId } = useParams() as { conversationId: string }
  const isMedium = useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg'))
  const [rightRailVisibilityStatus, setRightRailVisibilityStatus] =
    useState(!isMedium)
  const { t } = useTranslation(['Common', 'Communications'])

  const conversation = useSelector(getConversationById(conversationId))

  const { transport, client: conversationClientId } = conversation || {}

  const assignee = useSelector(getUser(conversation?.assigneeId))
  const conversationClient = useSelector(getUser(conversationClientId))
  const clientIsLoading = useSelector(getClientIsLoading)
  const isConversationNotAccessibleFromBusinessErrorType = useSelector(
    getConversationsIsNotAccessibleFromBusinessErrorType,
  )
  const isCommunicationsRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const isLogicalSMSThreadingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LOGICAL_SMS_THREADING),
  )

  const isContextItem = useIsCurrentContextItem(conversation)

  useEffect(() => {
    setRightRailVisibilityStatus(!isMedium)
  }, [isMedium])

  const {
    fields: { assigneeId },
    reset,
  } = useFields(
    [
      {
        name: 'assigneeId',
        label: isCommunicationsRightRailEnabled
          ? t('Common:ASSIGNED')
          : t('Common:ASSIGN_TEAM_MEMBER'),
        initialValue: conversation?.assigneeId,
      },
    ],
    false,
  )

  useEffect(() => {
    if (conversation?.assigneeId && !assignee) {
      dispatch(fetchMember(conversation?.assigneeId, true))
    }
  }, [conversation?.assigneeId])

  useEffect(() => {
    reset()
  }, [conversation])

  const isFullConversationClient = Boolean(conversationClient?.patients)

  useEffect(() => {
    if (conversationClientId && !clientIsLoading && !isFullConversationClient) {
      dispatch(fetchClient({ clientId: conversationClientId }))
    }
  }, [conversationClientId, clientIsLoading])

  useEffect(() => {
    if (isConversationNotAccessibleFromBusinessErrorType) {
      navigate('/communications')
    }
  }, [isConversationNotAccessibleFromBusinessErrorType])

  const withRightRail = rightRailVisibilityStatus || isMedium
  const isConversationLoaded = Boolean(transport)
  const hasPatient =
    isConversationLoaded &&
    Boolean(conversationClientId && conversation?.patient)
  const showNewRightRail =
    isCommunicationsRightRailEnabled && isConversationLoaded

  const messageInput = isLogicalSMSThreadingEnabled ? (
    <KibMessageFormattingArea conversationId={conversationId} />
  ) : (
    <Grid container item>
      <MessageFormattingArea conversationId={conversationId} />
    </Grid>
  )

  const showLegacyRightRail = isConversationLoaded && rightRailVisibilityStatus

  const chatContent = (
    <>
      <ContextualBackButton
        buttonText={t('Common:PRACTICE_INBOX')}
        navigateBackToContext={() => navigate('/communications')}
      />
      {isCommunicationsRightRailEnabled || isLogicalSMSThreadingEnabled ? (
        <KibChatHeader
          assigneeId={assigneeId}
          conversation={conversation}
          hasPatient={hasPatient}
        />
      ) : (
        <ChatHeader assigneeId={assigneeId} conversation={conversation} />
      )}
      <Stack className={classes.chat}>
        <Chat conversationId={conversationId} />
      </Stack>
      {isContextItem && messageInput}
    </>
  )

  return (
    <ChatContainerProvider key={conversationId}>
      <div
        className={
          showNewRightRail ? classes.gridContainer : classes.legacyContainer
        }
      >
        {showNewRightRail ? (
          <>
            <div className={classes.mainContentGrid}>{chatContent}</div>
            <div className={classes.rightRailGrid}>
              <RightRailOverlayProvider>
                <CommunicationsPatientMedicalRecordRightRail
                  conversationId={conversationId}
                />
              </RightRailOverlayProvider>
            </div>
          </>
        ) : (
          <div
            className={classNames(
              classes.legacyMainContent,
              !withRightRail && classes.rootWide,
            )}
          >
            {isConversationLoaded && (
              <ChatRightRail
                conversationId={conversationId}
                visible={showLegacyRightRail}
                onVisibilityChange={setRightRailVisibilityStatus}
              />
            )}
            {chatContent}
          </div>
        )}
      </div>
    </ChatContainerProvider>
  )
}

export default ChatContainer
